import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import * as yup from "yup";

import { useUpdateAllocationAmountForDealOpportunityGp } from "@vega-api/generated/coinvest-deal-opportunity-gp-actions/coinvest-deal-opportunity-gp-actions";
import { getGetDealTrackingForGpQueryKey } from "@vega-api/generated/coinvest-deal-tracking/coinvest-deal-tracking";
import {
  Currency,
  type Currency as CurrencyType,
} from "@vega-api/generated/types/currency";
import { MoneyField } from "@vega-core/components/Form/Fields/MoneyField";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { Notice } from "@vega-core/components/Notice/Notice";
import { useToast } from "@vega-core/components/Toast/useToast";
import { type CoreMoney } from "@vega-core/models/money";
import { m } from "@vega-core/paraglide/messages";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";

import { ModalButtonGroup } from "./ModalButtonGroup";

type AllocationAmountModalProps = {
  dealOpportunityId: string;
  dealId: string;
  dealCurrency?: CurrencyType;
  isOpen: boolean;
  onClose: () => void;
  allocationAmount?: CoreMoney;
  indicativeAmount?: CoreMoney;
};

type FormValues = {
  allocationAmount: CoreMoney;
};

const validationSchema = yup.object({
  allocationAmount: yup.object({
    amount: yup
      .number()
      .required(m.coInvestments_allocationAmount_required())
      .min(0),
    currency: yup.string().required(),
  }),
});

export const AllocationAmountModal = ({
  dealOpportunityId,
  dealId,
  dealCurrency = Currency.USD,
  isOpen,
  onClose,
  allocationAmount,
  indicativeAmount,
}: AllocationAmountModalProps) => {
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();
  const queryClient = useQueryClient();

  const { mutate: updateAllocationAmount, isPending } =
    useUpdateAllocationAmountForDealOpportunityGp({
      mutation: {
        onSuccess: () => {
          showToast({
            variant: "success",
            title: m.coInvestments_allocationAmount_success(),
          });
          queryClient.invalidateQueries({
            queryKey: getGetDealTrackingForGpQueryKey(dealId),
          });
          onClose();
        },
        onError: (error) => {
          showErrorToast(error, {
            defaultMessage: m.coInvestments_allocationAmount_error(),
          });
        },
      },
    });

  const getInitialValues = (): FormValues => ({
    allocationAmount: allocationAmount ??
      indicativeAmount ?? {
        amount: 0,
        currency: dealCurrency,
      },
  });

  const handleSubmit = (values: FormValues) => {
    updateAllocationAmount({
      dealOpportunityId,
      data: {
        allocationAmount: {
          amount: values.allocationAmount.amount,
          currency: values.allocationAmount.currency,
        },
      },
    });
  };

  return (
    <ActionaryModal
      dataTestId="allocation-amount-modal"
      fluidHeight
      open={isOpen}
      setOpen={onClose}
      title={m.coInvestments_termsAgreed_allocationAmount()}
    >
      <Formik
        enableReinitialize
        initialValues={getInitialValues()}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        <Form className="space-y-6">
          <MoneyField
            allowAbbreviations
            currency={dealCurrency}
            isRequired
            label={m.formFields_amount()}
            name="allocationAmount"
          />

          <Notice
            className="text-left"
            content={m.coInvestments_lpVisibilityWarning_description()}
            title={m.coInvestments_lpVisibilityWarning_title()}
            type="info"
            variant="accent"
          />

          <ModalButtonGroup
            cancelTestId="cancel-allocation-amount-button"
            confirmTestId="confirm-allocation-amount-button"
            isSubmitting={isPending}
            onCancel={onClose}
          />
        </Form>
      </Formik>
    </ActionaryModal>
  );
};
